import React, { useState, useEffect } from 'react';
import { X, Lightbulb, HelpCircle, Plus, ChevronDown, ChevronRight, Eye, EyeOff, RefreshCw, BookOpen } from 'lucide-react';
import { GuidedReadingResult, ReadingSegment } from '../services/geminiService';
import { Language } from '../types';

interface GuidedReadingPanelProps {
    result: GuidedReadingResult;
    language: Language;
    pageNumber: number;
    highlightsVisible: boolean;
    onToggleHighlights: () => void;
    onCreateNote: (title: string, prompt: string, segment?: ReadingSegment) => void;
    onSetHighlights: (items: { text: string; colorIndex: number }[]) => void;
    onRefresh: () => void;
    onClose: () => void;
}

const DOT_COLORS = ['bg-amber-400', 'bg-blue-500', 'bg-emerald-500', 'bg-purple-500'];

export const GuidedReadingPanel: React.FC<GuidedReadingPanelProps> = ({
    result,
    language,
    pageNumber,
    highlightsVisible,
    onToggleHighlights,
    onCreateNote,
    onSetHighlights,
    onRefresh,
    onClose,
}) => {
    const [expandedOrder, setExpandedOrder] = useState<number | null>(
        result.readingSegments[0]?.order ?? null
    );
    const [collapsed, setCollapsed] = useState(false);
    const isVi = language === 'vietnamese';

    // Sync highlights with expanded segment
    useEffect(() => {
        if (expandedOrder !== null) {
            const seg = result.readingSegments.find(s => s.order === expandedOrder);
            if (seg) {
                const ci = (seg.order - 1) % 4;
                onSetHighlights([
                    ...seg.keyHighlights.map(text => ({ text, colorIndex: ci })),
                    ...seg.keywords.map(text => ({ text, colorIndex: ci })),
                ]);
                return;
            }
        }
        // All segments' key highlights with per-segment colors
        onSetHighlights(
            result.readingSegments.flatMap(seg =>
                seg.keyHighlights.map(text => ({ text, colorIndex: (seg.order - 1) % 4 }))
            )
        );
    }, [expandedOrder, result]);

    const handleToggle = (order: number) => {
        setExpandedOrder(prev => prev === order ? null : order);
    };

    const overviewTitle = isVi ? `Tổng quan trang ${pageNumber}` : `Page ${pageNumber} overview`;
    const overviewPrompt = result.readingSegments
        .flatMap(s => s.guidingQuestions)
        .map((q, i) => `${i + 1}. ${q}`)
        .join('\n');

    // Collapsed state: thin vertical tab
    if (collapsed) {
        return (
            <div className="h-full w-9 shrink-0 border-l border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/10 flex flex-col">
                <button
                    onClick={() => setCollapsed(false)}
                    className="flex-1 flex flex-col items-center justify-start pt-4 gap-2 hover:bg-amber-100 dark:hover:bg-amber-900/20 transition-colors"
                    title={isVi ? 'Mở Gợi ý đọc' : 'Open Guided Reading'}
                >
                    <Lightbulb size={14} className="text-amber-500" />
                    <span
                        className="text-[9px] font-bold text-amber-700 dark:text-amber-300 select-none"
                        style={{ writingMode: 'vertical-rl', transform: 'rotate(180deg)' }}
                    >
                        {isVi ? 'Gợi ý đọc' : 'Guided Reading'}
                    </span>
                </button>
            </div>
        );
    }

    // Expanded docked rail
    return (
        <div className="h-full w-72 shrink-0 border-l border-amber-200 dark:border-amber-800 bg-white dark:bg-slate-900 flex flex-col overflow-hidden">
            {/* Header */}
            <div className="flex items-center gap-2 px-3 py-2 bg-amber-50 dark:bg-amber-900/20 border-b border-amber-100 dark:border-amber-900/40 shrink-0">
                <Lightbulb size={13} className="text-amber-500 shrink-0" />
                <span className="flex-1 text-xs font-bold text-amber-800 dark:text-amber-300 truncate">
                    {isVi ? `Gợi ý đọc — Trang ${pageNumber}` : `Guided Reading — Page ${pageNumber}`}
                </span>
                <div className="flex items-center gap-0.5">
                    <button
                        onClick={onToggleHighlights}
                        className={`p-1 rounded transition-colors ${highlightsVisible ? 'text-amber-600 dark:text-amber-400 bg-amber-100 dark:bg-amber-900/40' : 'text-gray-400 hover:text-gray-600 dark:hover:text-gray-300'}`}
                        title={isVi ? 'Bật/tắt highlight' : 'Toggle highlights'}
                    >
                        {highlightsVisible ? <Eye size={12} /> : <EyeOff size={12} />}
                    </button>
                    <button onClick={onRefresh} className="p-1 rounded text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors" title={isVi ? 'Gợi ý lại' : 'Refresh'}>
                        <RefreshCw size={12} />
                    </button>
                    <button onClick={() => setCollapsed(true)} className="p-1 rounded text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors" title={isVi ? 'Thu gọn' : 'Collapse'}>
                        <ChevronRight size={12} />
                    </button>
                    <button onClick={onClose} className="p-1 rounded text-gray-400 hover:text-red-500 transition-colors" title={isVi ? 'Đóng' : 'Close'}>
                        <X size={12} />
                    </button>
                </div>
            </div>

            {/* Section label */}
            <div className="px-3 py-1.5 flex items-center gap-1.5 border-b border-gray-100 dark:border-slate-800 shrink-0">
                <BookOpen size={10} className="text-gray-400" />
                <span className="text-[9px] font-bold uppercase tracking-wider text-gray-400">
                    {isVi ? 'Đọc theo thứ tự' : 'Read in order'}
                </span>
            </div>

            {/* Segment list */}
            <div className="flex-1 overflow-y-auto p-2 space-y-1.5 min-h-0">
                {result.readingSegments.map(seg => (
                    <SegmentBlock
                        key={seg.order}
                        segment={seg}
                        expanded={expandedOrder === seg.order}
                        onToggle={() => handleToggle(seg.order)}
                        language={language}
                        onCreateNote={onCreateNote}
                    />
                ))}
            </div>

            {/* Footer — secondary CTA */}
            <div className="px-2 pb-2 pt-1 border-t border-gray-100 dark:border-slate-800 shrink-0">
                <button
                    onClick={() => onCreateNote(overviewTitle, overviewPrompt)}
                    className="w-full flex items-center justify-center gap-1.5 py-1.5 text-[11px] font-semibold text-gray-400 dark:text-gray-500 hover:text-indigo-600 dark:hover:text-indigo-400 hover:bg-indigo-50 dark:hover:bg-indigo-900/20 rounded-xl transition-colors"
                >
                    <Plus size={11} />
                    {isVi ? 'Tạo note tổng quan trang này' : 'Create overview note for this page'}
                </button>
            </div>
        </div>
    );
};

const SegmentBlock: React.FC<{
    segment: ReadingSegment;
    expanded: boolean;
    onToggle: () => void;
    language: Language;
    onCreateNote: (title: string, prompt: string, segment?: ReadingSegment) => void;
}> = ({ segment, expanded, onToggle, language, onCreateNote }) => {
    const isVi = language === 'vietnamese';
    const dot = DOT_COLORS[(segment.order - 1) % 4];
    const borderExpanded = ['border-amber-200 dark:border-amber-800 bg-amber-50/50 dark:bg-amber-900/10',
        'border-blue-200 dark:border-blue-800 bg-blue-50/50 dark:bg-blue-900/10',
        'border-emerald-200 dark:border-emerald-800 bg-emerald-50/50 dark:bg-emerald-900/10',
        'border-purple-200 dark:border-purple-800 bg-purple-50/50 dark:bg-purple-900/10'][((segment.order - 1) % 4)];

    return (
        <div className={`rounded-xl border transition-colors ${expanded ? borderExpanded : 'border-gray-100 dark:border-slate-800 hover:border-gray-200 dark:hover:border-slate-700'}`}>
            <button onClick={onToggle} className="w-full flex items-start gap-2 px-2.5 py-2 text-left">
                <span className={`w-4 h-4 rounded-full shrink-0 flex items-center justify-center ${dot} text-white text-[9px] font-bold mt-0.5`}>{segment.order}</span>
                <div className="flex-1 min-w-0">
                    <div className="text-[10px] text-gray-400 dark:text-gray-500 font-medium">{segment.segmentLabel}</div>
                    <div className="text-xs font-semibold text-gray-800 dark:text-gray-100 leading-snug">{segment.title}</div>
                </div>
                {expanded ? <ChevronDown size={11} className="text-gray-400 shrink-0 mt-1" /> : <ChevronRight size={11} className="text-gray-400 shrink-0 mt-1" />}
            </button>

            {expanded && (
                <div className="px-2.5 pb-2.5 space-y-2.5">
                    {/* Writing task */}
                    <p className="text-[11px] text-indigo-700 dark:text-indigo-300 leading-relaxed font-medium">{segment.writingTask}</p>

                    {/* Why read together */}
                    <p className="text-[10px] text-gray-400 dark:text-gray-500 leading-relaxed">{segment.whyReadTogether}</p>

                    {/* Guiding questions */}
                    <div>
                        <div className="flex items-center gap-1 mb-1">
                            <HelpCircle size={10} className="text-indigo-500" />
                            <span className="text-[9px] font-bold uppercase tracking-wider text-indigo-600 dark:text-indigo-400">
                                {isVi ? 'Tự trả lời' : 'Answer yourself'}
                            </span>
                        </div>
                        <ul className="space-y-1">
                            {segment.guidingQuestions.map((q, i) => (
                                <li key={i} className="flex gap-1.5 items-start">
                                    <span className="text-indigo-400 font-bold text-[10px] shrink-0 mt-0.5">{i + 1}.</span>
                                    <span className="text-[11px] text-gray-600 dark:text-gray-300 leading-snug">{q}</span>
                                </li>
                            ))}
                        </ul>
                    </div>

                    {/* CTA */}
                    <button
                        onClick={() => onCreateNote(segment.suggestedNoteTitle, segment.notePrompt, segment)}
                        className="flex items-center gap-1 text-[10px] font-bold text-indigo-600 dark:text-indigo-400 hover:text-indigo-800 dark:hover:text-indigo-200 bg-indigo-50 dark:bg-indigo-900/30 hover:bg-indigo-100 dark:hover:bg-indigo-900/50 px-2 py-1 rounded-lg transition-colors"
                    >
                        <Plus size={10} />
                        {isVi ? 'Tạo note từ đoạn này' : 'Create note from this passage'}
                    </button>
                </div>
            )}
        </div>
    );
};
